import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { Plus, Settings } from 'lucide-react';

import NewChatButton from '@/components/header/NewChat';
import { useAppConfig } from '@/hooks/useAppConfig';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import PromptsModal from '@/components/PromptsModal';
import { kbApi, type KnowledgeBase } from '@/api/kb';
import { systemPromptsApi, type SystemPrompt } from '@/api/systemPrompts';
import { fastModeState, selectedKbIdState, selectedSystemPromptIdState } from '@/state/chat';


export default function ChatToolbar() {
  const navigate = useNavigate();
  // PromptsModal：目前是純前端、無持久化的舊視窗，UI 預設隱藏。
  // 保留作為「未來可能的系統提示詞編輯視窗」骨架——介面定案後，
  // 計畫把它改造成在聊天內開啟、編輯，並接上 /api/system-prompts 後端
  //（建立/更新/刪除 + 稽核已就緒，見 docs/SYSTEM_PROMPT_FEATURE_PLAN.md）。
  // 屆時把 SHOW_LEGACY_PROMPTS 改為 true 並改寫 PromptsModal 內部即可。
  const SHOW_LEGACY_PROMPTS = false;
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [kbList, setKbList] = useState<KnowledgeBase[]>([]);
  const [selectedKbId, setSelectedKbId] = useRecoilState(selectedKbIdState);

  // 實際在跑的模型名稱（由後端 LLM_MODEL env 決定，前端不寫死）
  const [llmModel, setLlmModel] = useState<string>('');

  // System Prompt 選擇
  const [promptList, setPromptList] = useState<SystemPrompt[]>([]);
  const [selectedSystemPromptId, setSelectedSystemPromptId] = useRecoilState(
    selectedSystemPromptIdState
  );

  useEffect(() => {
    kbApi.list().then(setKbList).catch(() => setKbList([]));
  }, []);

  // 載入後端實際使用的模型，顯示於模型下拉（與側欄共用同一份 /api/config 快取）
  const appConfig = useAppConfig();
  useEffect(() => {
    if (appConfig?.llm_model) setLlmModel(appConfig.llm_model);
  }, [appConfig]);

  // 快速模式開關：本視窗自己的狀態，初始值取後端預設；每則訊息隨 metadata 送出
  const [fastMode, setFastMode] = useRecoilState(fastModeState);
  useEffect(() => {
    if (fastMode === null && appConfig) {
      setFastMode(Boolean(appConfig.fast_path_default));
    }
  }, [appConfig, fastMode, setFastMode]);

  // 切換 KB 時：載入該 KB 的 Prompt 清單，並用 resolve 自動帶入上次使用/預設
  useEffect(() => {
    if (!selectedKbId || selectedKbId === 'none') {
      setPromptList([]);
      setSelectedSystemPromptId('default');
      return;
    }
    let cancelled = false;
    Promise.all([
      systemPromptsApi.list(selectedKbId).catch(() => [] as SystemPrompt[]),
      systemPromptsApi.resolve(selectedKbId).catch(() => ({ prompt: null })),
    ]).then(([list, resolved]) => {
      if (cancelled) return;
      setPromptList(list);
      setSelectedSystemPromptId(resolved.prompt ? resolved.prompt.id : 'default');
    });
    return () => {
      cancelled = true;
    };
  }, [selectedKbId, setSelectedSystemPromptId]);

  // 切換 Prompt：記錄「上次使用」（僅影響下一個新對話）
  const onSelectPrompt = (value: string) => {
    setSelectedSystemPromptId(value);
    if (selectedKbId && selectedKbId !== 'none') {
      systemPromptsApi
        .setUsage(selectedKbId, value === 'default' ? null : value)
        .catch(() => {});
    }
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-card flex-wrap">

        {/* 歷史紀錄與新對話入口皆移至左側欄（AppSidebar）與聊天區懸浮按鈕 */}

        {/* 2. 語言模型（後端 LLM_MODEL 決定，唯讀顯示） */}
        <Select value={llmModel || undefined} disabled>
          <SelectTrigger className="w-56">
            <SelectValue placeholder={llmModel ? undefined : '載入模型中…'} />
          </SelectTrigger>
          <SelectContent>
            {llmModel && (
              <SelectGroup>
                <SelectLabel className="text-xs text-muted-foreground">語言模型</SelectLabel>
                <SelectItem value={llmModel}>{llmModel}</SelectItem>
              </SelectGroup>
            )}
          </SelectContent>
        </Select>

        {/* 3. 知識庫選擇 */}
        <Select value={selectedKbId} onValueChange={setSelectedKbId}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="不使用知識庫" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">不使用知識庫</SelectItem>
            {kbList.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-xs text-muted-foreground">我的知識庫</SelectLabel>
                {kbList.map((kb) => (
                  <SelectItem key={kb.id} value={kb.id}>
                    {kb.name}
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({kb.file_count} 檔)
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>

        {/* 4. System Prompt 選擇（僅在已選 KB 時顯示） */}
        {selectedKbId !== 'none' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Select value={selectedSystemPromptId} onValueChange={onSelectPrompt}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="系統提示詞" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">預設（不指定）</SelectItem>
                  {promptList.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="text-xs text-muted-foreground">
                        系統提示詞
                      </SelectLabel>
                      {promptList.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                          {p.is_default && (
                            <span className="ml-1 text-xs text-muted-foreground">（預設）</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </TooltipTrigger>
            <TooltipContent>如欲更換提示詞，請重新開啟對話</TooltipContent>
          </Tooltip>
        )}

        {/* 5. 快速模式開關（僅在已選 KB 時顯示；只影響本視窗之後送出的問題） */}
        {selectedKbId !== 'none' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <label className="flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-background text-sm cursor-pointer select-none">
                <Switch
                  checked={fastMode === true}
                  onCheckedChange={(v) => setFastMode(Boolean(v))}
                  aria-label="快速模式"
                />
                <span>快速模式</span>
              </label>
            </TooltipTrigger>
            <TooltipContent>
              開：直接從最相關的章節作答，較快。關：完整檢索與答案查核，較完整。下一題起生效。
            </TooltipContent>
          </Tooltip>
        )}

        {/* 新對話：常駐工具列右側（右上角），行動版縮成圓形圖示不遮內文 */}
        <div className="ml-auto flex-shrink-0">
          <NewChatButton
            navigate={(to) => navigate(to)}
            icon={<Plus className="!size-5" />}
            className="h-9 w-9 sm:w-auto sm:px-4 gap-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
          >
            <span className="hidden sm:inline text-sm font-medium whitespace-nowrap">
              新對話
            </span>
          </NewChatButton>
        </div>

        {/* 舊版 Prompts 管理：保留程式碼，預設隱藏（見 SHOW_LEGACY_PROMPTS） */}
        {SHOW_LEGACY_PROMPTS && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="flex-shrink-0"
                  onClick={() => setPromptsOpen(true)}
                >
                  <Settings className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>管理 Prompts</TooltipContent>
            </Tooltip>

            <PromptsModal open={promptsOpen} onOpenChange={setPromptsOpen} />
          </>
        )}

      </div>
    </TooltipProvider>
  );
}
